/*
 * JC cart drawer (snippets/cart-drawer.liquid, styles in assets/jc-cart-drawer.css).
 * Uses Shopify's cart Ajax API: /cart/change for quantities and removal, /cart/add to undo a
 * removal, and the Section Rendering API (sections cart-drawer and cart-icon-bubble) so the
 * drawer, totals and bag count always show what the server has.
 * - Open: slides in from the right, page scroll locked (no layout shift: the scrollbar's width
 *   is kept as padding), focus trapped, Esc / overlay / X close it, focus returns to the bag.
 * - Updating a line: that row dims and its number shows "…" until the new totals arrive.
 * - Stock limit: the quantity stays and the row says "Only X left in this size."
 * - Remove: an undo bar ("Removed {title}" + Undo) sits at the top of the list for about 6s or
 *   until the next action; Undo adds the same variant and quantity back.
 * - Checkout: the button shows a spinner and "Going to checkout" while the form posts.
 * The element keeps the API the theme's add-to-cart code uses: open(), renderContents(),
 * getSectionsToRender(), setActiveElement().
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('#CartDrawer');
      this.status = this.querySelector('[data-jc-cd-status]');
      this.queue = Promise.resolve();
      this.undoState = null;
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          e.stopPropagation();
          this.close();
        } else if (e.key === 'Tab') this.trap(e);
      });
      this.addEventListener('submit', (e) => {
        if (e.target.matches('[data-jc-cd-form]')) this.onCheckout(e.target);
      });
      window.addEventListener('pageshow', (e) => {
        // back from checkout (page restored from the back/forward cache): reset the button
        if (!e.persisted) return;
        const btn = this.querySelector('[data-jc-cd-checkout]');
        if (btn) this.resetCheckout(btn);
      });

      this.setHeaderCartIconAccessibility();

      // cart changes made elsewhere (the cart page, other add-to-cart code): refresh
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (['jc-cart-drawer', 'product-form', 'external-refresh'].includes(event && event.source)) return;
          this.refresh();
        });
      }
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    setHeaderCartIconAccessibility() {
      const bag = document.querySelector('#cart-icon-bubble');
      if (!bag) return;
      bag.setAttribute('role', 'button');
      bag.setAttribute('aria-haspopup', 'dialog');
      bag.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(bag);
      });
      bag.addEventListener('keydown', (e) => {
        if (e.key === ' ') {
          e.preventDefault();
          this.open(bag);
        }
      });
    }

    setActiveElement(el) {
      this.activeElement = el;
    }

    open(trigger) {
      if (trigger) this.setActiveElement(trigger);
      if (!this.activeElement || !document.contains(this.activeElement)) this.activeElement = document.querySelector('#cart-icon-bubble');
      this.fillBest();
      if (this.isOpen) return;
      // keep the page from shifting when its scrollbar goes away
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--jc-sbw', `${Math.max(0, sbw)}px`);
      document.documentElement.classList.add('jc-scroll-lock', 'jc-cd-lock');
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      // next frame, so the slide always runs from the closed position
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const close = this.querySelector('.jc-cd__close');
        (close || this.panel).focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      document.documentElement.classList.remove('jc-scroll-lock', 'jc-cd-lock');
      const back = this.activeElement && document.contains(this.activeElement) ? this.activeElement : document.querySelector('#cart-icon-bubble');
      if (back) back.focus({ preventScroll: true });
    }

    trap(e) {
      const items = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null && !el.closest('[inert]')
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    fillBest() {
      const grid = this.querySelector('[data-jc-cd-best]');
      const tpl = this.querySelector('[data-jc-cd-best-tpl]');
      if (grid && tpl && !grid.children.length) grid.appendChild(tpl.content.cloneNode(true));
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    /* after an add to cart (product form, quick add): show the new state and open */
    renderContents(parsed) {
      this.dismissUndo();
      if (parsed && parsed.sections) this.apply(parsed.sections);
      this.open();
      const key = parsed && (parsed.key || (parsed.items && parsed.items[0] && parsed.items[0].key));
      requestAnimationFrame(() => {
        const row = key && this.querySelector(`[data-jc-cd-item][data-key="${CSS.escape(key)}"]`);
        if (row) row.scrollIntoView({ block: 'nearest' });
        else {
          const body = this.querySelector('[data-jc-cd-body]');
          if (body) body.scrollTop = 0;
        }
      });
    }

    /* swap in freshly rendered sections, keeping scroll position and focus */
    apply(sections) {
      const html = sections['cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-jc-cd-content]');
        const current = this.querySelector('[data-jc-cd-content]');
        if (fresh && current) {
          const oldBody = this.querySelector('[data-jc-cd-body]');
          const scroll = oldBody ? oldBody.scrollTop : 0;
          const focus = this.focusDescriptor();
          current.replaceWith(fresh);
          const body = this.querySelector('[data-jc-cd-body]');
          if (body) body.scrollTop = scroll;
          this.classList.toggle('is-empty', fresh.dataset.count === '0');
          this.renderUndo();
          if (this.isOpen) this.fillBest();
          this.restoreFocus(focus);
        }
      }
      const bubble = sections['cart-icon-bubble'];
      const bag = document.getElementById('cart-icon-bubble');
      if (bubble && bag) {
        const src = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
        if (src) bag.innerHTML = src.innerHTML;
      }
    }

    focusDescriptor() {
      const a = document.activeElement;
      if (!a || !this.panel.contains(a)) return null;
      const row = a.closest('[data-jc-cd-item]');
      let role = 'other';
      if (a.matches('[data-jc-cd-step="1"]')) role = 'inc';
      else if (a.matches('[data-jc-cd-step="-1"]')) role = 'dec';
      else if (a.matches('[data-jc-cd-remove]')) role = 'remove';
      else if (a.matches('[data-jc-cd-undo]')) role = 'undo';
      return { key: row && row.dataset.key, role };
    }

    restoreFocus(d) {
      if (!d || !this.isOpen) return;
      const row = d.key && this.querySelector(`[data-jc-cd-item][data-key="${CSS.escape(d.key)}"]`);
      const map = { inc: '[data-jc-cd-step="1"]', dec: '[data-jc-cd-step="-1"]', remove: '[data-jc-cd-remove]' };
      let target = row && map[d.role] && row.querySelector(map[d.role]);
      if (!target) target = this.querySelector('[data-jc-cd-undo]') || this.querySelector('.jc-cd__close');
      if (target) target.focus({ preventScroll: true });
    }

    onClick(e) {
      if (e.target.closest('[data-jc-cd-close]')) {
        this.close();
        return;
      }
      const undo = e.target.closest('[data-jc-cd-undo]');
      if (undo) {
        this.undo();
        return;
      }
      const row = e.target.closest('[data-jc-cd-item]');
      if (!row || row.classList.contains('is-updating')) return;
      const step = e.target.closest('[data-jc-cd-step]');
      if (step) {
        const qty = Math.max(0, parseInt(row.dataset.qty, 10) + parseInt(step.dataset.jcCdStep, 10));
        this.change(row, qty);
      } else if (e.target.closest('[data-jc-cd-remove]')) {
        this.change(row, 0);
      }
    }

    say(text) {
      if (!this.status) return;
      this.status.textContent = '';
      requestAnimationFrame(() => (this.status.textContent = text));
    }

    stockMessage(row, n) {
      const el = row && row.querySelector('[data-jc-cd-stock]');
      if (!el) return;
      const text = n > 0 ? `Only ${n} left in this size.` : 'No more left in this size.';
      el.textContent = text;
      row.classList.add('has-stock-msg');
      this.say(text);
    }

    clearMessages() {
      this.querySelectorAll('[data-jc-cd-stock]').forEach((el) => (el.textContent = ''));
      this.querySelectorAll('.has-stock-msg').forEach((el) => el.classList.remove('has-stock-msg'));
    }

    sectionsParams() {
      return { sections: this.getSectionsToRender().map((s) => s.id).join(','), sections_url: window.location.pathname };
    }

    post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(Object.assign(body, this.sectionsParams())),
      }).then((r) => r.json().then((json) => ({ ok: r.ok, status: r.status, json })));
    }

    change(row, qty) {
      const key = row.dataset.key;
      const before = parseInt(row.dataset.qty, 10);
      const removing = qty === 0;
      const removed = removing
        ? {
            id: parseInt(row.dataset.variant, 10),
            quantity: before,
            title: row.dataset.title,
            properties: (() => {
              try {
                return JSON.parse(row.dataset.props || '{}') || {};
              } catch (err) {
                return {};
              }
            })(),
            selling_plan: row.dataset.plan ? parseInt(row.dataset.plan, 10) : undefined,
          }
        : null;
      this.dismissUndo();
      this.clearMessages();
      row.classList.add('is-updating');
      const num = row.querySelector('[data-jc-cd-num]');
      if (num) num.textContent = '…';

      const changeUrl = `${(window.routes && window.routes.cart_change_url) || '/cart/change'}.js`;
      this.queue = this.queue
        .then(() => this.post(changeUrl, { id: key, quantity: qty }))
        .then(({ ok, status, json }) => {
          if (!ok || json.status) {
            // the store said no (usually not enough stock): keep the quantity, say why
            this.restoreRow(row, before);
            if (status === 422 || json.status === 422) {
              const m = /(?:only|all)\s+(\d+)/i.exec(json.description || json.message || '');
              this.stockMessage(row, m ? parseInt(m[1], 10) : before);
            } else {
              this.say('Sorry, the cart could not be updated. Please try again.');
            }
            return;
          }
          // a removal: the undo bar is part of the new render, so focus can land on Undo
          if (removed) this.undoState = removed;
          this.apply(json.sections || {});
          if (removed) {
            this.startUndoTimer();
            this.say(`Removed ${removed.title}`);
          } else {
            const line = (json.items || []).find((i) => i.key === key);
            const fresh = this.querySelector(`[data-jc-cd-item][data-key="${CSS.escape(key)}"]`);
            if (line && line.quantity < qty) this.stockMessage(fresh, line.quantity);
            else this.say(`Quantity ${qty}`);
          }
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jc-cart-drawer', cartData: json });
          }
        })
        .catch(() => {
          this.restoreRow(row, before);
          this.say('Sorry, the cart could not be updated. Please try again.');
        });
    }

    restoreRow(row, qty) {
      if (!document.contains(row)) return;
      row.classList.remove('is-updating');
      const num = row.querySelector('[data-jc-cd-num]');
      if (num) num.textContent = qty;
    }

    /* ---- undo bar ---- */
    startUndoTimer() {
      clearTimeout(this.undoTimer);
      this.undoTimer = setTimeout(() => this.dismissUndo(), 6000);
    }

    renderUndo() {
      const slot = this.querySelector('[data-jc-cd-undo-slot]');
      if (!slot) return;
      slot.textContent = '';
      if (!this.undoState) return;
      const bar = document.createElement('div');
      bar.className = 'jc-cd__undo';
      const text = document.createElement('span');
      text.className = 'jc-cd__undo-text';
      text.textContent = `Removed ${this.undoState.title}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jc-cd__undo-btn';
      btn.setAttribute('data-jc-cd-undo', '');
      btn.textContent = 'Undo';
      btn.setAttribute('aria-label', `Undo: add ${this.undoState.title} back`);
      bar.append(text, btn);
      slot.appendChild(bar);
    }

    dismissUndo() {
      clearTimeout(this.undoTimer);
      if (!this.undoState) return;
      const hadFocus = this.panel.contains(document.activeElement) && document.activeElement.matches('[data-jc-cd-undo]');
      this.undoState = null;
      this.renderUndo();
      if (hadFocus) {
        const close = this.querySelector('.jc-cd__close');
        if (close) close.focus({ preventScroll: true });
      }
    }

    undo() {
      const item = this.undoState;
      if (!item) return;
      clearTimeout(this.undoTimer);
      const btn = this.querySelector('[data-jc-cd-undo]');
      if (btn) btn.disabled = true;
      const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
      const line = { id: item.id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
      if (item.selling_plan) line.selling_plan = item.selling_plan;
      this.queue = this.queue
        .then(() => this.post(addUrl, { items: [line] }))
        .then(({ ok, json }) => {
          this.undoState = null;
          if (!ok || json.status) {
            this.renderUndo();
            this.say(json.description || 'Sorry, this item could not be added back.');
            const close = this.querySelector('.jc-cd__close');
            if (close) close.focus({ preventScroll: true });
            return;
          }
          this.apply(json.sections || {});
          this.say(`${item.title} is back in your cart`);
          const key = json.items && json.items[0] && json.items[0].key;
          const row = key && this.querySelector(`[data-jc-cd-item][data-key="${CSS.escape(key)}"]`);
          const target = (row && row.querySelector('[data-jc-cd-remove]')) || this.querySelector('.jc-cd__close');
          if (target) target.focus({ preventScroll: true });
          if (row) row.scrollIntoView({ block: 'nearest' });
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jc-cart-drawer', cartData: json });
          }
        })
        .catch(() => {
          if (btn && document.contains(btn)) btn.disabled = false;
          this.say('Sorry, this item could not be added back.');
        });
    }

    /* ---- refresh after changes made elsewhere ---- */
    refresh() {
      const url = `${(window.routes && window.routes.cart_url) || '/cart'}?sections=${this.getSectionsToRender()
        .map((s) => s.id)
        .join(',')}`;
      return fetch(url)
        .then((r) => r.json())
        .then((sections) => this.apply(sections))
        .catch(() => {});
    }

    /* ---- checkout ---- */
    onCheckout(form) {
      const btn = form.querySelector('[data-jc-cd-checkout]');
      if (!btn) return;
      // the hidden "checkout" field carries the request, so the button can be disabled now
      requestAnimationFrame(() => {
        btn.disabled = true;
        btn.classList.add('is-loading');
        const label = btn.querySelector('[data-jc-cd-checkout-label]');
        if (label) {
          btn.dataset.label = label.textContent;
          label.textContent = 'Going to checkout';
        }
      });
    }

    resetCheckout(btn) {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      const label = btn.querySelector('[data-jc-cd-checkout-label]');
      if (label && btn.dataset.label) label.textContent = btn.dataset.label;
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}
